package br.gov.go.pge.permanenciaweb.shared

/*
 * Text folding shared by the frontend (column filters, name search) and the backend (directory
 * search). The producer DAG folds `nome_busca` with `unidecode` before pushing the directory, so
 * this table must agree with it for names that carry accents, ordinals or cedillas.
 */

/**
 * Lowercase entries only: [normalizeForSearch] lowercases first, so `Ç` and `ç` both end up here
 * as `ç`.
 *
 * `º`/`ª` are folded because ordinals are pervasive in room/floor labels ("1º andar") and users
 * type "1o andar".
 */
private val SEARCH_FOLDS: Map<Char, Char> = buildMap {
    fun fold(target: Char, chars: String) = chars.forEach { put(it, target) }
    fold('a', "áàâãäå")
    fold('e', "éèêë")
    fold('i', "íìîï")
    fold('o', "óòôõö")
    fold('u', "úùûü")
    fold('c', "ç")
    fold('n', "ñ")
    fold('o', "º")
    fold('a', "ª")
}

/** Trim → lowercase → fold diacritics. Applied to both sides of a comparison. */
fun normalizeForSearch(value: String?): String {
    val trimmed = value?.trim().orEmpty()
    if (trimmed.isEmpty()) return ""
    val lower = trimmed.lowercase()
    return buildString(lower.length) {
        for (ch in lower) append(SEARCH_FOLDS[ch] ?: ch)
    }
}

/** Case- and accent-insensitive substring match (`LIKE '%query%'`). A blank query matches all. */
fun matchesContains(value: String?, query: String): Boolean {
    val needle = normalizeForSearch(query)
    if (needle.isEmpty()) return true
    return normalizeForSearch(value).contains(needle)
}
